import { useState, FormEvent } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import { ProductReview } from '../../../mocks/productReviews';
import { submitReviewForModeration } from '../../../utils/reviewStorage';

interface ReviewFormProps {
  beverageId: string;
  beverageName: string;
  onSubmit: (review: ProductReview) => void;
  onCancel: () => void;
}

const avatars = [
  'https://public.readdy.ai/ai/img_res/96e7cd892e233bb0a86c3fd1f797ea0d.jpg',
  'https://readdy.ai/api/search-image?query=person%20friendly%20smile%20portrait%20headshot%20neutral%20background%20casual&width=80&height=80&seq=form2&orientation=squarish',
  'https://readdy.ai/api/search-image?query=person%20confident%20smile%20portrait%20headshot%20neutral%20background%20warm&width=80&height=80&seq=form3&orientation=squarish',
];

export default function ReviewForm({ beverageId, beverageName, onSubmit, onCancel }: ReviewFormProps) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const ratingLabels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
  const MAX_CHARS = 500;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name required', 'Please enter your name.'); return; }
    if (rating === 0) { toast.error('Rating required', 'Please select a star rating.'); return; }
    if (comment.trim().length < 10) { toast.error('Review too short', 'Please write at least 10 characters.'); return; }

    const newReview: ProductReview = {
      id: `user_${Date.now()}`,
      beverageId,
      userName: name.trim(),
      avatar: avatars[Math.floor(Math.random() * avatars.length)],
      rating,
      title: title.trim(),
      comment: comment.trim(),
      date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      verified: false,
      helpful: 0,
    };

    // Send to moderation queue instead of publishing directly
    submitReviewForModeration(newReview, beverageName);

    setSubmitted(true);
    onSubmit(newReview);
  };

  if (submitted) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-100 text-amber-500 mx-auto mb-3">
          <i className="ri-time-line text-2xl" />
        </div>
        <h4 className="text-base font-bold text-amber-900 mb-1">Review submitted!</h4>
        <p className="text-sm text-amber-700 leading-relaxed">
          Thank you for your feedback. Your review is currently <strong>awaiting moderation</strong> and will appear once approved.
        </p>
      </div>
    );
  }

  const activeRating = hoverRating || rating;

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6">
      <h4 className="text-base font-bold text-gray-900 mb-5">Write a Review</h4>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Your Name</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-user-line text-sm" />
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] transition-all bg-[#fafaf8] text-gray-800 placeholder-gray-400"
            />
          </div>
        </div>

        {/* Star rating picker */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Your Rating</label>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onMouseEnter={() => setHoverRating(i + 1)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(i + 1)}
                  className={`w-8 h-8 flex items-center justify-center transition-all cursor-pointer ${
                    i < activeRating ? 'text-amber-400 scale-110' : 'text-gray-200 hover:text-amber-300'
                  }`}
                >
                  <i className="ri-star-fill text-2xl" />
                </button>
              ))}
            </div>
            {activeRating > 0 && (
              <span className={`text-sm font-semibold transition-all ${
                activeRating >= 4 ? 'text-green-600' : activeRating === 3 ? 'text-amber-500' : 'text-red-400'
              }`}>
                {ratingLabels[activeRating]}
              </span>
            )}
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Review Title <span className="text-gray-400 font-normal normal-case">(optional)</span></label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Summarize your experience..."
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] transition-all bg-[#fafaf8] text-gray-800 placeholder-gray-400"
          />
        </div>

        {/* Comment */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Your Review</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, MAX_CHARS))}
            placeholder={`What did you think of ${beverageName}? Share your experience...`}
            rows={4}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] transition-all bg-[#fafaf8] text-gray-800 placeholder-gray-400 resize-none"
          />
          <div className={`text-right text-xs mt-1 ${comment.length >= MAX_CHARS ? 'text-red-400' : 'text-gray-400'}`}>
            {comment.length}/{MAX_CHARS}
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="submit"
            className="flex-1 bg-[#FF6B35] hover:bg-[#e85d2a] text-white font-semibold py-3 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            Post Review
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-5 border border-gray-200 text-gray-600 font-medium py-3 rounded-xl text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
